import { RefreshCw } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { sync } from "../../actions/banners";
import { SYNC_STATUS_CHANGED_EVENT } from "../../actions/tamaracSyncStatus";
import { popover } from "../../helpers/util";
import DefaultMenu from "./DefaultMenu";
import LinkWithRole from "./LinkWithRole";
import WithRole from "./WithRole";

const SYNC_STATUS_CODES = {
  0: { label: "Synced", tone: "success" },
  1: { label: "Syncing…", tone: "warning" },
  2: { label: "Delayed", tone: "danger" },
};

const renderUserMenu = (user, impersonated, syncStatus) => {
  const company = user.Company;
  const syncStatusConfig = SYNC_STATUS_CODES[syncStatus?.status];
  return (
    <ul className="nav navbar-nav navbar-toolbar app-nav-end header-toolbar-icons">
      {syncStatus && syncStatusConfig && (
        <li className="header-toolbar-icon sync-status-toolbar-item">
          <button
            type="button"
            aria-label={`Synchronization status: ${syncStatusConfig.label} — ${syncStatus.message}`}
            className="navbar-toolbar-icon waves-effect waves-light"
            data-app-popover="true"
            data-content="#SyncStatusPopoverContent"
            data-placement="left"
            data-trigger="hover"
          >
            <span className={`sync-status-pill sync-status-pill--${syncStatusConfig.tone}`}>
              <span className="sync-status-dot" aria-hidden="true" />
              <span className="sync-status-text">{syncStatusConfig.label}</span>
              <RefreshCw className="sync-status-mobile-icon" aria-hidden="true" size={18} strokeWidth={2.25} />
            </span>
          </button>
        </li>
      )}
      <li className="dropdown header-toolbar-icon">
        <a
          className="navbar-avatar dropdown-toggle waves-effect waves-light"
          data-bs-toggle="dropdown"
          data-bs-auto-close="outside"
          href="# "
          data-animation="scale-up"
        >
          <span className="avatar">
            <img src="/images/global/default-avatar-80.png" alt="Profile" />
          </span>
        </a>
        <ul className="dropdown-menu dropdown-menu-end position-absolute">
          <li role="presentation">
            <Link to={`/user/${user.id}/edit`} role="menuitem" className="dropdown-item waves-effect waves-light">
              <i className="icon icon_profile" aria-hidden="true" /> Profile
            </Link>
          </li>
          {company?.id && (
            <li role="presentation">
              <LinkWithRole
                authorize="companies"
                action="edit"
                to={`/company/${company.id}/edit`}
                role="menuitem"
                className="dropdown-item waves-effect waves-light"
              >
                <i className="icon fa fa-building-o" aria-hidden="true" /> Company
              </LinkWithRole>
            </li>
          )}
          {/*
            <li role="presentation">
              <a href="https://assetdedication.zendesk.com/hc/en-us" target="_blank" rel="noopener noreferrer" role="menuitem"><i className="icon icon_question_alt" aria-hidden="true" /> Support</a>
            </li>
          */}
          {!impersonated && (
            <li role="presentation">
              <WithRole authorize="users" action="impersonate">
                <a
                  role="menuitem"
                  data-bs-target="#ImpersonateModal"
                  data-bs-toggle="modal"
                  className="dropdown-item waves-effect waves-light"
                  href="# "
                >
                  <i className="icon fa fa-user" aria-hidden="true" /> Impersonate
                </a>
              </WithRole>
            </li>
          )}
          <li className="divider" role="presentation" />
          <li role="presentation">
            <Link to="/signout" role="menuitem" className="dropdown-item waves-effect waves-light">
              <i className="icon icon_lock_alt" aria-hidden="true" /> {impersonated ? "End Impersonate" : "Logout"}
            </Link>
          </li>
        </ul>
      </li>
    </ul>
  );
};

const Header = ({ show, impersonated, user, location }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  // oxlint-disable-next-line react/exhaustive-deps -- refetch sync status on every location change
  useEffect(() => {
    const fetchSyncStatus = async () => {
      if (!show) {
        return;
      }
      try {
        const response = await sync();
        setSyncStatus(response);
      } catch (err) {
        console.error(err);
      }
    };
    fetchSyncStatus();
    window.addEventListener(SYNC_STATUS_CHANGED_EVENT, fetchSyncStatus);
    return () => window.removeEventListener(SYNC_STATUS_CHANGED_EVENT, fetchSyncStatus);
  }, [show, location]);
  useEffect(() => {
    if (syncStatus) {
      popover();
    }
  }, [syncStatus]);
  if (!show) {
    return null;
  }
  return (
    <div className="main-header navbar navbar-default fixed-top navbar-inverse navbar-mega">
      <div className="navbar-header">
        <Link className="navbar-brand" to="/">
          <img
            className="navbar-brand-logo navbar-brand-logo-normal"
            src="/images/logo-header.png"
            title="Asset Dedication"
            alt="logo"
          />
        </Link>
      </div>
      <div className="navbar-container app-container-fluid">
        <div
          className="collapse navbar-collapse app-nav-collapse navbar-block navbar-collapse-toolbar"
          id="site-navbar-collapse"
        >
          <div className="utmenucontainer">
            <div className="overlapblackbg" />
            <a id="wsnavtoggle" className="animated-arrow" href="# ">
              <span />
            </a>
            <nav className="utmenu">
              <DefaultMenu user={user} />
            </nav>
          </div>
          {renderUserMenu(user, impersonated, syncStatus)}
          {syncStatus && (
            <div id="SyncStatusPopoverContent" className="hidden">
              {syncStatus.message}
            </div>
          )}
        </div>
      </div>
      {impersonated && (
        <div className="impersonate-alert">
          <div>
            You are impersonating {user.first_name} {user.last_name}. To stop impersonating the user,{" "}
            <Link to="/signout">click here</Link>.
          </div>
        </div>
      )}
    </div>
  );
};

Header.propTypes = {
  show: PropTypes.bool,
  user: PropTypes.object,
  impersonated: PropTypes.bool,
};

export default Header;
